import {
  applySizeToImageUrl,
  Button,
  ButtonSize,
  ButtonVariant,
  Device,
  ImageSizeVariant,
  ResponsiveTypography,
  ResponsiveTypographyVariant,
  Scale,
  useDeviceViewport,
  withTheme,
} from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';
import * as moment from 'moment';
import * as React from 'react';
import { TranslationsContext } from '../../common/contexts';
import { ApplicationContext, IApplication } from '../../common/contexts/ApplicationContext';
import { IBooking, IBookingStatus } from '../../common/types/apiSchema';
import { getLocaleSettings } from '../../common/utils/getLocaleSettingsUtils';
import { LoadingSkeletonSection } from '../../common/utils/loadingSkeletons.styles';
import { SaleCardVariant } from '../SaleCard/SaleCard.interfaces';
import { AspectRatioWrapper } from '../SaleCard/SaleCardSkeleton/SaleCardSkeleton.styles';
import { createSaleLabelProperties } from '../SaleLabel/SaleLabel.helpers';
import SaleLabelList, { SaleLabelVariant, TSaleLabelRenderPropsArray } from '../SaleLabelList';
import {
  IBookingCardAmendedDatesProps,
  IBookingCardImageProps,
  IBookingCardLabelsProps,
  IBookingCardProps,
} from './BookingCard.interfaces';
import {
  AmendedDatesText,
  BookingLabelsContainer,
  CustomDetailLine,
  CustomNormalImage,
  ImageContainer,
} from './BookingCard.styles';

const DATE_FORMAT: string = 'ddd DD MMM YYYY';

const BookingCardImage: React.FunctionComponent<IBookingCardImageProps> = ({ leadImage }) => {
  const device: Device = useDeviceViewport();
  let imageUrl: string = '';
  const size: { width: number; height: number } = BookingCardImageSize[device];
  if (leadImage) {
    imageUrl = leadImage.urlWithSize
      ? applySizeToImageUrl(leadImage.urlWithSize, size.width, size.height)
      : leadImage.url;
  }

  return (
    <ImageContainer variant={SaleCardVariant.normal}>
      {leadImage && (
        <CustomNormalImage
          fallback={
            <AspectRatioWrapper>
              <LoadingSkeletonSection
                marginBottom={Scale.m}
                width="100%"
                height="100%"
                background={true}
                borderRadius={`${Scale.xxs} ${Scale.xxs} 0 0`}
              />
            </AspectRatioWrapper>
          }
          alt={leadImage.caption}
          size={ImageSizeVariant.fluid}
          srcSet={[{ src: imageUrl }]}
        />
      )}
    </ImageContainer>
  );
};

const BookingCardLabels: React.FunctionComponent<IBookingCardLabelsProps> = withTheme(props => {
  const { theme, amended, cancelled } = props;
  const { translations } = React.useContext(TranslationsContext);
  const labelProperties: TSaleLabelRenderPropsArray = [
    {
      backgroundColor: theme.color.secondary60,
      saleLabelVariant: SaleLabelVariant.normal,
      show: amended,
      translationKey: 'booking.amended.label',
    },
    {
      backgroundColor: theme.color.error60,
      saleLabelVariant: SaleLabelVariant.normal,
      show: cancelled,
      translationKey: 'cancellation.cancelled.booking.label',
    },
  ].filter(o => o.show);

  return (
    <div>
      {labelProperties.length > 0 && (
        <BookingLabelsContainer>
          <SaleLabelList
            saleLabelListProperties={createSaleLabelProperties(labelProperties, translations)}
            variant={SaleLabelVariant.small}
          />
        </BookingLabelsContainer>
      )}
    </div>
  );
});

const BookingCardDetails: React.FunctionComponent<IBookingCardProps> = ({ booking }) => {
  const { translations } = React.useContext(TranslationsContext);
  const applicationProps: IApplication = React.useContext(ApplicationContext);
  const locale: string = getLocaleSettings(applicationProps.territoryConfig.posLocale);
  const startDate: string = moment(
    booking.amendedDates ? booking.amendedDates.start : booking.bookingDates.start
  )
    .locale(locale)
    .format(DATE_FORMAT);
  const endDate: string = moment(
    booking.amendedDates ? booking.amendedDates.end : booking.bookingDates.end
  )
    .locale(locale)
    .format(DATE_FORMAT);
  const dates: string = `${startDate} - ${endDate} | `;
  const details: string = `${i18n('common.numberOfRooms.text', [booking.rooms], translations)} |
    ${i18n('common.nights.text', [booking.duration], translations)}`;
  const detailsLine: string = dates + details;

  const amended: boolean = booking.amendedDates !== null;
  const cancelled: boolean = booking.status === IBookingStatus.CANCELLED;

  return (
    <CustomDetailLine>
      <BookingCardLabels amended={amended} cancelled={cancelled} />
      <ResponsiveTypography variant={ResponsiveTypographyVariant.micro}>
        {detailsLine}
      </ResponsiveTypography>
      <BookingCardAmendedDates booking={booking} variant={ResponsiveTypographyVariant.micro} />
    </CustomDetailLine>
  );
};

const BookingCardAmendedDates: React.FunctionComponent<IBookingCardAmendedDatesProps> = withTheme(
  props => {
    const { booking, variant, theme } = props;
    const applicationProps: IApplication = React.useContext(ApplicationContext);
    const locale: string = getLocaleSettings(applicationProps.territoryConfig.posLocale);
    const startDate: string = moment(booking.bookingDates.start)
      .locale(locale)
      .format(DATE_FORMAT);
    const endDate: string = moment(booking.bookingDates.end)
      .locale(locale)
      .format(DATE_FORMAT);
    const oldDates: string = `${startDate} - ${endDate}`;

    return (
      <div>
        {booking.amendedDates && (
          <AmendedDatesText variant={variant} color={theme.color.grey20}>
            {oldDates}
          </AmendedDatesText>
        )}
      </div>
    );
  }
);

/**
 * onClick in button is mandatory but because the whole booking card takes the click,
 * this doesn't need to do anything.
 */
/*istanbul ignore next */
const onClick: () => void = () => {
  return;
};

const BookingCardButton: React.FunctionComponent<IBookingCardProps> = ({ booking }) => {
  const device: Device = useDeviceViewport();
  const { translations } = React.useContext(TranslationsContext);
  const translationKey: string = getBookingButtonText(booking);

  return (
    <Button
      variant={ButtonVariant.secondary}
      size={ButtonSize.medium}
      onClick={onClick}
      fullWidth={Device.mobile === device}
    >
      {i18n(translationKey, [], translations)}
    </Button>
  );
};

const BookingCardImageSize: {
  [d in Device]: { height: number; width: number };
} = {
  [Device.mobile]: { width: 518, height: 360 },
  [Device.tablet]: { width: 324, height: 216 },
  [Device.desktop]: { width: 324, height: 216 },
};

const getBookingButtonText: (booking: IBooking) => string = booking => {
  if (
    booking.status !== IBookingStatus.UPCOMING &&
    booking.links.viewExternalBookingDetails === null
  ) {
    return 'view.booking';
  } else {
    return 'view.change.booking';
  }
};

export { BookingCardButton, BookingCardDetails, BookingCardImage };
